"use client"

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle, DialogTrigger } from "../ui/dialog";
import { useCallback, useEffect, useMemo, useState } from "react";
import { RecoveryView } from "@/app/deployment/recovery/page";
import { recovercheck, recovercleanup, recovergetbackup, recoversqldata, recoveruploadbackupcontent } from "@/services/recovery.service";
import { Input } from "../ui/input";
import { RecoveryStatus } from "@/constants/status";
import { Button } from "../ui/button";
import { checkInputFlipBoolean } from "@/helpers/input.helper";
import Dropdown, { dropdownCss, DropdownInterface } from "../ui/dropdown";
import { cn } from "@/lib/utils";

interface Props {
    domain: RecoveryView,
    updateData: () => void,
    approvedConnections: DropdownInterface[]
}

export default function RecoveryModal(props: Props) {
    const [open, setOpen] = useState(false)
    const [recoveryStatus, setRecoveryStatus] = useState(RecoveryStatus.standBy);
    const [failedMessage, setFailedMessage] = useState("");
    const [backupName, setBackupName] = useState("");
    const [currentStep, setCurrentstep] = useState(-1);
    const [recoveryDetails, setRecoverydetails] = useState<Map<string, string>>();
    const [isDisabled, setIsDisabled] = useState(true);

    const steps = useMemo(() => [
        {
            title: "Is Recovery Available",
            expectedReturn: "Recovery Available",
            functionToRun: recovercheck,
            data: []
        },
        {
            title: "Fetch Backup",
            expectedReturn: "Backup Fetched",
            functionToRun: recovergetbackup,
            data: []
        },
        {
            title: "Recover SQL data",
            expectedReturn: "SQL data recovered",
            functionToRun: recoversqldata,
            data: []
        },
        {
            title: "Upload Backup files",
            expectedReturn: "Backup files uploaded",
            functionToRun: recoveruploadbackupcontent,
            data: []
        },
        {
            title: "Recovery Cleanup",
            expectedReturn: "Recovery Complete",
            functionToRun: recovercleanup,
            data: []
        },
    ], []);

    const formatFormData = useCallback(
        () => {
          const formData = new FormData();
          recoveryDetails?.forEach((value, key) => {
            formData.append(key, value);
          })
          return formData;
        },
        [recoveryDetails]
    );

    async function initiateRecovery(domainID: string){
        setRecoveryStatus(RecoveryStatus.onGoing);

        document.getElementById("initiationbox-" + domainID)?.setAttribute("hidden", "true");

        const backupName = (
            document.getElementById("recovery-".concat(props.domain.ID)) as HTMLInputElement
        ).value;

        const ftpConnection = (
            document.getElementById("recovery-clearbookFTPConnection-".concat(props.domain.ID)) as HTMLInputElement
        ).value;

        const recoveryDetails = new Map<string, string>();
        recoveryDetails.set('domainID', domainID);
        recoveryDetails.set('backupName', backupName);
        recoveryDetails.set('ftpConnection', ftpConnection);

        setRecoverydetails(recoveryDetails);

        if (currentStep < 0) {
            setCurrentstep((index) => index + 1);
        }

        return;
    }

    const nextStep = useCallback(() => {
        setCurrentstep((index) => index + 1);
    }, []);

    const handleSuccess = useCallback(() => {
        setCurrentstep(-1);
        setIsDisabled(true);
        setRecoveryStatus(RecoveryStatus.standBy);
        props.updateData();
        setOpen(false);
    }, [props]);

    const startStep = useCallback(async () => {
        if(steps[currentStep].functionToRun != undefined){
            let result = '';
            try {
                result = await steps[currentStep].functionToRun(
                    formatFormData()
                );
            } catch (error) {
                console.log(result);
                setFailedMessage("Recovery failed. Check console.log for more information.");
                setRecoveryStatus(RecoveryStatus.failed);
                return;
            }

            if(result == steps[currentStep].expectedReturn){
                if (currentStep < steps.length - 1){
                    nextStep();
                } else {
                    handleSuccess();
                }
            } else {
                console.log(result);
                setFailedMessage("Recovery failed. Check console.log for more information.<br>Otherwise, here: ".concat(result));
                setRecoveryStatus(RecoveryStatus.failed);
                return;
            }
        }
    }, [
        currentStep,
        nextStep,
        formatFormData,
        steps,
        handleSuccess
    ]);

    useEffect(() => {
        if (currentStep > -1) {
            startStep();
        }
    }, [currentStep, startStep]);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className={props.domain.Backups.length > 0 ? "cursor-pointer" : ""} disabled={props.domain.Backups.length <= 0} onClick={() => {
                    const result = (
                        document.getElementById("recovery-".concat(props.domain.ID)) as HTMLInputElement
                    ).value;

                    setBackupName(result);
                }}>Recover Domain</Button>
            </DialogTrigger>
            <DialogContent className="max-w-sm">
                <DialogTitle>Recover Domain</DialogTitle>
                <DialogDescription>
                    <span>You sure you want to recover domain based on: {backupName} backup?</span> 
                </DialogDescription>
                <h1 className="text-xl font-bold text-black dark:text-white mb-2">
                    FTP Connection
                </h1>
                <Dropdown
                    id={"recovery-clearbookFTPConnection-".concat(props.domain.ID)}
                    title="FTP Connection"
                    className={cn(dropdownCss, "w-auto")}
                >
                    {props.approvedConnections}
                </Dropdown>
                <DialogFooter>
                    <div className="grid grid-cols-6 gap-4">
                        <div className="col-start-1 col-end-7 rounded-md border-black">
                            {recoveryStatus != RecoveryStatus.onGoing && recoveryStatus != RecoveryStatus.completed && (
                                <div className="flex flex-col w-full max-w-2xl  gap-y-4" id={"initiationbox-" + props.domain.ID}>
                                    <Input title="CONFIRM" placeholder="Enter CONFIRM" onChange={(e) => checkInputFlipBoolean(e.target.value, setIsDisabled)}/>
                                    <Button id={"initiationRecoveryButton-".concat(props.domain.ID)} disabled={isDisabled} className="cursor-pointer mb-3" onClick={async (_e) => await initiateRecovery(props.domain.ID)}>Recover Domain</Button>
                                </div>
                            )}
                            {recoveryStatus == RecoveryStatus.onGoing && (
                                <div className="flex flex-col w-full max-w-2xl  gap-y-4">
                                    <p>Recovering...</p>
                                </div>
                            )}
                            {recoveryStatus == RecoveryStatus.completed && (
                                <div className="flex flex-col w-full max-w-2xl  gap-y-4">
                                    <p>Recovery Complete! Will refresh this page momentarily</p>
                                </div>
                            )}
                            {recoveryStatus == RecoveryStatus.failed && (
                                <div className="flex flex-col w-full max-w-2xl  gap-y-4">
                                    <p>{failedMessage}</p>
                                </div>
                            )}

                            {currentStep > -1 && (
                                <p>{steps[currentStep].title}</p>
                            )}
                        </div>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}